import { BaseActionsProps, BaseState, FilterParams } from "@/store/modules/helpers/baseState";
import { delayedReset } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";
import { resetDelayedResetTimeout } from "./delayedReset";

export type SubsetCommon = {
    ids: {
        [key: number]: number | string;
    };
};

export type Subsets = {
    default: SubsetCommon;
    newlyCreated: SubsetCommon;
    newlyUpdated: SubsetCommon;
    [key: string]: SubsetCommon & {
        filter?: {
            [key: string]: unknown;
        };
    };
};

export type BaseSubsetState = {
    subsets: Subsets;
    subsetTotals: { [key: string]: number };
    subscribers: number;
};

export type BaseSubsetActions = {
    addSubscriber: (props: BaseActionsProps<BaseState>) => Promise<void>;
    removeSubscriber: (props: BaseActionsProps<BaseState>) => Promise<void>;
};

export type BaseSubsetMutations = {
    addSubscriber: (state: BaseState) => void;
    removeSubscriber: (state: BaseState) => void;
    setTotal: (state: BaseState, props: { total: number; subsetName: string }) => void;
    resetLocalSubsets: (state: BaseState) => void;
};

export type SubsetGetterProps<STATE> = {
    state: BaseState & STATE;
    collectionName: keyof STATE;
    offset: number;
    subsetName?: keyof Subsets;
    limit?: number;
    // Returnera som array. false returnerar en Map
    toArray?: boolean;
    /*
        Funktion som anropas när det ska avgöras om nya tillagda items ska hamna i vissa subsets. Kan användas för
        att konvertera query params sin inte har direkt match mot strukturen i data (t.ex show_struken=false som ska
        kolla på status !== "Struken")
     */
    filterParamConverter?: (props: { filterParams: FilterParams }) => FilterParams;
    /**
     * Lägger in items från subset newlyCreated först i listan som hämtas (på page 1)
     */
    prependNewlyCreated?: boolean;
};

export type SubsetTotalGetterProps<STATE> = {
    state: BaseState & STATE;
    subsetName?: keyof Subsets;
};

export type SubsetSetterProps<STATE, VALUE> = Omit<SubsetGetterProps<STATE>, "limit" | "subsetName"> & {
    items: (VALUE & {
        id: number | string;
    })[];
    subsetName: keyof Subsets;
    // Rensa subset innan set
    resetSubset?: boolean;
    /**
     * Totala mängden av träffar från BE vid paginering. Subsetet kommer att kapas för att matcha total om det
     * skickas med och överflödet kommer tas bort
     */
    total?: number;
    /**
     * Ska items även sättas in i och tas bort ur subsetet "default", dvs den ofiltrerade listan? Med den
     * behandlas default som en hämtning utan filter, så en ny post hamnar överst där på samma sätt som i en
     * filtrerad lista. Utan den rörs bara subset som har ett filter i sitt namn, och en ny post syns i den
     * ofiltrerade listan först vid nästa hämtning.
     *
     * Skickas med från moduler vars listor läser ur "default" när inget filter är satt. Utelämnas för
     * moduler som alltid hämtar med filter, där default inte visas.
     */
    includeDefaultSubset?: boolean;
};

export type SubsetUpdateSetterProps<STATE, VALUE> = SubsetGetterProps<STATE> & {
    items: (VALUE & {
        id: number | string;
    })[];
    /**
     * Subsets som INTE kommer filtrera bort uppdaterade items. Kan användas för att behålla
     * precis uppdaterade rader i en lista. Default är att filtrering görs direkt på alla subsets
     */
    frozenSubsets?: string[];
    /**
     * Ska ALLA subsets låta bli att filtrera bort uppdaterade items?
     */
    freezeAllSubsets?: boolean;
    includeDefaultSubset?: SubsetSetterProps<STATE, VALUE>["includeDefaultSubset"];
};

export type SubsetRemoveProps<STATE> = {
    collectionName: SubsetGetterProps<STATE>["collectionName"];
    ids: string[] | number[];
    state: BaseState & STATE;
};

// Filtreringsregler som kan skickas med params till BE
enum FilterRule {
    GreaterThan = "gt",
    EqualOrGreaterThan = "gte",
    LessThan = "lt",
    EqualOrLessThan = "lte",
    Any = "any",
    NotEqual = "ne",
}

const ignoredFilter = ["offset", "limit", "ordering"];

/**
 * Gemensamma state för subsets. Ärvs av baseState
 */
export const baseSubsetState: BaseSubsetState = {
    subsets: {
        // Hämtning av objekt med standard filtrering/sortering
        default: {
            ids: {},
        }, // Nyskapade objekt kommer hamna i detta subset
        newlyCreated: {
            ids: {},
        }, // Nyligen redigerade objekt kommer hamna i detta subset
        newlyUpdated: {
            ids: {},
        },
    }, // Antal komponenter som använder sig av data från modulen i store
    subsetTotals: {},
    subscribers: 0,
};

/**
 * Sorterar subsettet
 */
export const toSortedSubsetIds: (props: { subsetIds: SubsetCommon["ids"] }) => [number, number | string][] = ({
    subsetIds,
}) => {
    const sortedSubset = Object.entries(subsetIds);

    sortedSubset.sort(([aIndex], [bIndex]) => (Number(aIndex) > Number(bIndex) ? 1 : -1));

    return sortedSubset.map(([index, id]) => [Number(index), id]);
};

/**
 * Gemensamma actions för subset som ärvs av baseActions.
 * moduleName används för att registrera delayed reset
 */
export const baseSubsetActions: (props: {
    moduleName: string;
    onRemoveSubscriber?: (args: any) => void;
}) => BaseSubsetActions = ({ moduleName, onRemoveSubscriber }) => ({
    addSubscriber: async ({ commit }) => {
        resetDelayedResetTimeout({ moduleName });

        commit("addSubscriber");
    },
    removeSubscriber: async (args) => {
        const { state, commit } = args;

        commit("removeSubscriber");

        if (state.subscribers < 1) {
            delayedReset({
                commit,
                moduleName,
            });
        }

        onRemoveSubscriber?.(args);
    },
});

/**
 * Gemensamma mutations för subset. Ärvs av baseMutations
 */
export const baseSubsetMutations: BaseSubsetMutations = {
    addSubscriber: (state) => {
        state.subscribers = state.subscribers + 1;
    },
    removeSubscriber: (state) => {
        const newSubscribers = state.subscribers - 1;

        state.subscribers = newSubscribers >= 0 ? newSubscribers : 0;
    },
    setTotal: (state, { total, subsetName }) => {
        state.subsetTotals = {
            ...cloneDeep(state.subsetTotals),
            [subsetName]: total,
        };
    },
    /**
     * Rensar subsets som innehåller items baserat på vad användaren gjort i systemet (t.ex nyligen skapade/nyligen
     * uppdaterade items)
     */
    resetLocalSubsets: (state) => {
        const subsets = cloneDeep(state.subsets);

        state.subsets = {
            ...subsets,
            newlyCreated: {
                ids: {},
            },
            newlyUpdated: {
                ids: {},
            },
        };
    },
};

/**
 * Gemensamma gettern för subset. Ska anropas av vardera modul i store
 */
export const subsetGetter = <STATE>({
    offset,
    state,
    collectionName,
    prependNewlyCreated,
    toArray = true,
    subsetName = "default",
    limit = 20,
}: SubsetGetterProps<STATE>) => {
    const sentSubsetName = subsetName as NonNullable<typeof subsetName>;

    if (!state.subsets[subsetName]) {
        return toArray ? [] : new Map();
    }

    // Bygger upp subsetet genom att gå igenom id-listan och hämta ut värden från collection
    let subset = Object.entries(state.subsets[sentSubsetName].ids).reduce((subset, [index, id]) => {
        const value = (state[collectionName] as Map<number | string, unknown>).get(id);

        // Om limit är 0 (obegränsat) eller indexet ligger inom offset och limit, inkludera värdet
        if (limit === 0 || (value && Number(index) >= offset && Number(index) < offset + limit)) {
            return [...subset, [id, value]];
        }

        // Annars, hoppa över värdet
        return subset;
    }, [] as Array<any>);

    // Lägg till nyligen skapade i början av listan
    if (offset === 0 && prependNewlyCreated) {
        // Hämtar alla nyligen skapade items och lägger till i subset. Mappar om till en array av [id, value] för att
        // kunna slå ihop med subset som också är i det formatet
        const newlyCreatedSubset = Object.entries(state.subsets.newlyCreated.ids).map(([_, id]) => [
            id,
            (state[collectionName] as Map<number | string, unknown>).get(id),
        ]);
        // Använder Map för att ta bort dubbletter ifall ett nyligen skapat item också finns i det vanliga subset:et.
        // Mappar sedan tillbaka till en array av [id, value]
        subset = [...new Map([...newlyCreatedSubset, ...subset]).entries()];
    } else {
        // Använder Map för att ta bort dubbletter ifall samma item finns flera gånger i subset:et.
        // Mappar sedan tillbaka till en array av [id, value]
        subset = [...new Map(subset).entries()];
    }

    // Returnera antingen en array av värden eller en Map beroende på toArray-flaggan
    return toArray ? subset.map(([_, value]) => value) : new Map(subset);
};

/**
 * Antalet rader som finns för ett subset, alltså det värde en paginator ska räkna på.
 *
 * setTotal sätter värdet från backends antal, och addToSubset räknar upp det när frontenden själv lägger
 * till ett nyskapat item i subsetet. Komponenter läser antalet härifrån, så det följer samma state som
 * raderna.
 */
export const subsetTotalGetter = <STATE>({ state, subsetName = "default" }: SubsetTotalGetterProps<STATE>) =>
    state.subsetTotals[subsetName] ?? 0;

/**
 * Kollar om ny data matchar alla filter params inom en subset
 */
const filterItem = <STATE, VALUE>({
    filter,
    item,
    filterParamConverter = ({ filterParams }) => filterParams,
}: {
    item: SubsetSetterProps<STATE, VALUE>["items"][0];
    filter: FilterParams;
    filterParamConverter: SubsetSetterProps<STATE, VALUE>["filterParamConverter"];
}) => {
    return Object.entries(filterParamConverter({ filterParams: filter }))
        .filter(([key]) => !ignoredFilter.includes(key))
        .every(([key, value]) => {
            // Splittar på __. __ indikerar en nivå ner i objektet eller filtert som skickas till BE
            const splitFilterKey = key.split("__");
            // Kollar om sista värdet är regel eller bara värde
            const hasRule = Object.values(FilterRule).includes(splitFilterKey.at(-1) as FilterRule);

            /*
            Gräver ner i objektet och hämtar ut värdet från item.
            Grävningen krävs för att t.ex hantera vardplatsbestallning__patient__id som representerar
         */
            const valueInItem = (hasRule ? splitFilterKey.slice(0, -1) : splitFilterKey).reduce(
                (filteredItem, name: any) => {
                    return filteredItem[name];
                },
                item as any
            );

            // Typar om value, så att TS slutar gnälla. En dag ska vi uppgradera TS och resten av deps...
            const compareValue = value as string | number | Array<string | number>;

            /*
                Kollar vilken regel som matchas och gör relevant jämförelse.
                Om valueInItem är objekt antas det att det ska göras en direkt jämförelse och att det är mot
                valueInItem.id
             */
            if (hasRule && typeof valueInItem !== "object") {
                const rule = splitFilterKey.at(-1);

                if (rule === FilterRule.EqualOrGreaterThan) {
                    return valueInItem >= compareValue;
                }

                if (rule === FilterRule.GreaterThan) {
                    return valueInItem > compareValue;
                }

                if (rule === FilterRule.EqualOrLessThan) {
                    return valueInItem <= compareValue;
                }

                if (rule === FilterRule.LessThan) {
                    return valueInItem < compareValue;
                }

                if (rule === FilterRule.NotEqual) {
                    return valueInItem !== compareValue;
                }

                if (rule === FilterRule.Any) {
                    return true;
                }
            }

            /*
                Om det är en array som är value kollar vi att minst ett värde i den matchar
             */
            if (Array.isArray(valueInItem)) {
                return valueInItem.includes(compareValue);
            }

            /*
                Jämför värdet rakt av om regel inte var satt. Om det är ett objekt antas det att det ska
                 jämföras mot valueInItem.id
             */
            if (typeof valueInItem === "object") {
                return valueInItem.id === compareValue;
            }

            return Array.isArray(compareValue) ? compareValue.includes(valueInItem) : valueInItem === compareValue;
        });
};

/**
 * Filtrerar bort subset names som inte är stringified json och mappar om till parsed json.
 *
 * Används av addToSubsetByFilter och removeFromSubsetByFilter för att få fram vilka subset ett item ska
 * matchas mot: varje subsetname med filter i är en hämtning från backend, och filtret avgör om itemet hör
 * dit eller inte.
 *
 * newlyCreated och newlyUpdated är inga hämtningar utan lokala listor med precis skapade respektive sparade
 * items, som ska ligga kvar oavsett filter (se resetLocalSubsets och prependNewlyCreated). De ska därför
 * aldrig med. default är däremot hämtningen utan filter, se convertToSubsetName, och kan väljas in med
 * includeDefault.
 */
const getSubsets = <STATE>({
    state,
    exclude = [],
    includeDefault,
}: {
    state: BaseState & STATE;
    /**
     * Subsetnames som ska hoppas över trots att de har ett filter. Skicka med det subset items redan
     * hanterats i (newlyCreated/newlyUpdated) och de subset som ska frysas från filtrering.
     */
    exclude?: (keyof Subsets)[];
    /**
     * Ta även med "default" trots att namnet inte är json. Den får då ett tomt filter, som allt matchar,
     * vilket gör att default behandlas som en hämtning utan filter. Skickas vidare från
     * includeDefaultSubset, se SubsetSetterProps.
     */
    includeDefault?: boolean;
}): { subsetName: keyof Subsets; filter: FilterParams }[] =>
    Object.keys(state.subsets)
        .filter((subsetName) => !exclude.includes(subsetName))
        .filter(
            (subsetName) =>
                (includeDefault && subsetName === "default") ||
                (subsetName.charAt(0) === "{" && subsetName.charAt(subsetName.length - 1) === "}")
        )
        .map((subsetName) => ({
            subsetName,
            filter: subsetName === "default" ? {} : JSON.parse(subsetName),
        }));

/**
 * Lägger till item till subset baserat på värden i item och subsetets filter (som är dess subset name)
 */
const addToSubsetByFilter = <STATE, VALUE>({
    items,
    state,
    currentSubsetName,
    filterParamConverter,
    itemsAreNew,
    includeDefaultSubset,
}: {
    items: SubsetSetterProps<STATE, VALUE>["items"];
    state: SubsetSetterProps<STATE, VALUE>["state"];
    filterParamConverter: SubsetSetterProps<STATE, VALUE>["filterParamConverter"];
    currentSubsetName: SubsetSetterProps<STATE, VALUE>["subsetName"];
    /**
     * Sant när items är nyskapade och därför inte ingår i antalet backend rapporterat. Då räknas subsetets
     * antal upp för varje item som läggs till. Sätts av subsetSetter för subsetet newlyCreated och ska
     * lämnas utanför i alla andra fall, framför allt från subsetUpdateSetter: ett uppdaterat item ingår
     * redan i backends antal och skulle räknas dubbelt.
     */
    itemsAreNew?: boolean;
    includeDefaultSubset?: boolean;
}) => {
    getSubsets({ state, exclude: [currentSubsetName], includeDefault: includeDefaultSubset }).forEach(
        ({ subsetName, filter }) => {
            // Filtrerar bort alla items som inte matchar filter
            const matchingItems = items.filter((item) => {
                return filterItem({ filter, filterParamConverter, item });
            });

            if (matchingItems.length) {
                addToSubset({ state, items: matchingItems, subsetName, itemsAreNew });
            }
        }
    );
};

/**
 * Tar bort items baserat på filter. Används vid uppdateringar
 */
const removeFromSubsetByFilter = <STATE, VALUE>({
    items,
    state,
    filterParamConverter,
    frozenSubsets = [],
    includeDefaultSubset,
}: {
    items: SubsetSetterProps<STATE, VALUE>["items"];
    state: SubsetSetterProps<STATE, VALUE>["state"];
    filterParamConverter: SubsetSetterProps<STATE, VALUE>["filterParamConverter"];
    frozenSubsets: SubsetUpdateSetterProps<STATE, VALUE>["frozenSubsets"];
    includeDefaultSubset?: boolean;
}) => {
    getSubsets({ state, exclude: frozenSubsets, includeDefault: includeDefaultSubset }).forEach(
        ({ subsetName, filter }) => {
            const subsetValues = Object.values(state.subsets[subsetName].ids);
            // Samling av alla items som inte matchar filter i subset det är med i
            const mismatchedItems = items.filter((item) => {
                return subsetValues.includes(item.id) && !filterItem({ filter, filterParamConverter, item });
            });

            if (mismatchedItems.length) {
                removeFromSubset({ state, items: mismatchedItems, subsetName });
            }
        }
    );
};

/**
 * Gemensamma gettern för subset. Ska anropas av vardera modul i store
 */
export const subsetSetter = <STATE, VALUE>({
    state,
    items,
    offset,
    collectionName,
    subsetName,
    resetSubset,
    filterParamConverter,
    total,
    includeDefaultSubset,
}: SubsetSetterProps<STATE, VALUE>) => {
    // Hämtar befintligt innehåll från state[collectionName] eller skapar en tom Map om den inte finns,
    // lägger till/uppdaterar dessa med nya items.
    const newCollection = new Map<number, (typeof items)[0]>([
        ...((state[collectionName] as Map<number, (typeof items)[0]>) ?? new Map()).entries(),
        ...items.map((item) => [item.id, item]),
    ] as [number, (typeof items)[0]][]);

    // Den nya Map:en sparas tillbaka i state under rätt namn.
    state[collectionName] = newCollection as (typeof state)[typeof collectionName];

    // Skapar ett objekt där nycklarna är index (med offset) och värdet är id:n.
    const additionalSubsetIds = Object.fromEntries(items.map((item, index) => [index + offset, item.id]));

    let ids = {};

    // Kopierar befintliga id:n om subset:et inte ska nollställas.
    if (state.subsets[subsetName]?.ids && !resetSubset) {
        ids = {
            ...cloneDeep(state.subsets[subsetName].ids),
        };
    }

    // Nya id:n läggs till (eller skriver över) i ids-objektet.
    ids = {
        ...ids,
        ...additionalSubsetIds,
    };

    /*
     * Index som överstiger totala mängden som BE sagt finns för en viss sökning rensas bort.
     * Det är för att se till att frysta subsets som egentligen innehåller fler items än det borde finnas
     * enligt filtreringen kapas. Tidigare var sista item alltid kvar i subsetet och det här löser det.
     */
    if (typeof total === "number") {
        ids = Object.fromEntries(Object.entries(ids).filter(([index]) => Number(index) < total));
    }

    //  Uppdaterar state med det nya subset:et och dess id:n.
    state.subsets = {
        ...cloneDeep(state.subsets),
        [subsetName]: {
            ids,
        },
    };

    // Tillägg ska bara läggas till i andra subsets om det är nyskapade, annars görs det via subsetUpdateSetter
    if (subsetName === "newlyCreated") {
        addToSubsetByFilter({
            state,
            items,
            filterParamConverter,
            currentSubsetName: subsetName,
            itemsAreNew: true,
            includeDefaultSubset,
        });
    }
};

/**
 * Tar bort items från ett subset
 */
const removeFromSubset = <STATE, VALUE>({
    state,
    items,
    subsetName,
}: {
    state: SubsetUpdateSetterProps<STATE, VALUE>["state"];
    items: SubsetUpdateSetterProps<STATE, VALUE>["items"];
    subsetName: SubsetSetterProps<STATE, VALUE>["subsetName"];
}) => {
    const subset = state.subsets[subsetName];

    if (!subset) {
        return;
    }

    const idsToRemove = items.map((item) => item.id);
    const updatedSubsetIds = toSortedSubsetIds({ subsetIds: subset.ids }).filter(
        ([_, id]) => !idsToRemove.includes(id)
    );

    state.subsets = {
        ...cloneDeep(state.subsets),
        [subsetName]: cloneDeep({
            ...cloneDeep(state.subsets[subsetName]),
            ids: Object.fromEntries(updatedSubsetIds),
        }),
    };
};

/**
 * Lägger till nya items till subset
 */
const addToSubset = <STATE, VALUE>({
    state,
    items,
    subsetName,
    itemsAreNew,
}: {
    state: SubsetUpdateSetterProps<STATE, VALUE>["state"];
    items: SubsetUpdateSetterProps<STATE, VALUE>["items"];
    subsetName: SubsetSetterProps<STATE, VALUE>["subsetName"];
    /**
     * Är items nyskapade, dvs finns de inte i antalet backend rapporterat? Bara då ska antalet räknas upp,
     * och bara med de items som faktiskt saknas i subsetet. Ett uppdaterat item ingår redan i backends
     * antal även om det inte råkar ligga i subsetets ids, så där ska flaggan utelämnas.
     */
    itemsAreNew?: boolean;
}) => {
    const subset = state.subsets[subsetName];

    if (!subset) {
        return;
    }

    const currentSubsetIds = toSortedSubsetIds({ subsetIds: subset.ids });

    const newSubsetIds: [number, number | string][] = items
        .filter((item: (typeof items)[0]) => !currentSubsetIds.find(([_, id]) => item.id === id))
        .map((item: (typeof items)[0], index) => [index, item.id]);

    const combinedIds: [number, number | string][] = [
        ...newSubsetIds,
        ...currentSubsetIds.map(([index, id]) => [index + newSubsetIds.length, id] as [number, string | number]),
    ];

    /*
        Antalet hålls i takt med id-listan. Bara subset som har ett spårat antal berörs, vilket lämnar
        newlyCreated/newlyUpdated utanför. Nästa hämtning skriver över värdet via setTotal.
     */
    if (itemsAreNew && newSubsetIds.length && typeof state.subsetTotals[subsetName] === "number") {
        state.subsetTotals = {
            ...cloneDeep(state.subsetTotals),
            [subsetName]: state.subsetTotals[subsetName] + newSubsetIds.length,
        };
    }

    state.subsets = {
        ...cloneDeep(state.subsets),
        [subsetName]: cloneDeep({
            ...cloneDeep(state.subsets[subsetName]),
            ids: Object.fromEntries(combinedIds),
        }),
    };
};

/**
 * Gemensamma update settern för subsets. Ska anropas av vardera modul i store
 */

export const subsetUpdateSetter = <STATE, VALUE>({
    state,
    items,
    collectionName,
    filterParamConverter,
    frozenSubsets,
    freezeAllSubsets,
    includeDefaultSubset,
}: SubsetUpdateSetterProps<STATE, VALUE>) => {
    const updatedCollection = new Map<number, (typeof items)[0]>([
        ...(state[collectionName] as Map<number, unknown>).entries(),
        ...items.map((item) => [item.id, item]),
    ] as [number, (typeof items)[0]][]);

    state[collectionName] = updatedCollection as (typeof state)[typeof collectionName];

    addToSubset({ state, items, subsetName: "newlyUpdated" });

    if (!freezeAllSubsets) {
        removeFromSubsetByFilter({ items, state, filterParamConverter, frozenSubsets, includeDefaultSubset });
    }

    addToSubsetByFilter({
        items,
        state,
        filterParamConverter,
        currentSubsetName: "newlyUpdated",
        includeDefaultSubset,
    });
};

export const subsetRemover = <STATE>({ ids, collectionName, state }: SubsetRemoveProps<STATE>) => {
    const collection = new Map(state[collectionName] as any);
    const idsArray: (string | number)[] = Array.isArray(ids) ? ids : [ids];

    // Ta bort alla objekt baserat på id
    idsArray.forEach((id) => {
        collection.delete(id);
    });

    state[collectionName] = collection as any;

    /*
        Filtera alla id:n i ett subset och ta bort de inskickade ids
     */
    const filteredSubsetEntries = Object.entries(state.subsets).map(([subsetName, subset]) => {
        const filteredIds = Object.entries(subset.ids).filter(([_, subsetId]) => !idsArray.includes(subsetId));

        return [subsetName, { ...subset, ids: Object.fromEntries(filteredIds) }];
    });

    state.subsets = cloneDeep(Object.fromEntries(filteredSubsetEntries));
};

/**
 * Konvertera inskickat objekt med filtreringsparametrar till en sträng för användning som key vid skapande
 * av subset. Kan parsas som json för att få ut filtrering och sorteringsvärden.
 */
export const convertToSubsetName: (props: { params?: unknown }) => keyof Subsets = ({ params } = {}) => {
    if (!params || !Object.keys(params).length) {
        return "default";
    }

    const sortedEntries = Object.entries(params).sort((a, b) => (a[0] > b[0] ? 1 : -1));
    return JSON.stringify(Object.fromEntries(sortedEntries)) as keyof Subsets;
};
